import { useGetGiveaway } from '@bpm-web-app/swr-hooks';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { appendQueryParams, getCurrentPlatformLink, getGiveawayCookie, useHubSwitch, showToast } from '@bpm-web-app/utils';
import { useForm } from 'react-hook-form';
import { useCallback, useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import { supremeBasePath } from 'libs/api-client/src/lib/config';
import styles from './enter-giveaway.module.css';
import useGiveaway from '../shared/three-dots-sheet/useGiveaway';
import CloseIcon from '../../assets/icons/close.svg';
import Title from '../title/title';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface EnterGiveawayProps { }

export function EnterGiveaway(props: EnterGiveawayProps) {
    const router = useRouter();
    const { slug } = router.query;

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showBonus, setShowBonus] = useState(false);
    const [entry, setEntry] = useState(null);
    const { hub } = useHubSwitch();

    const closeModal = () => {
        setShowBonus(false);
        router.replace(getCurrentPlatformLink(`/giveaway/${slug}`));
    };

    const { data, isLoading, error } = useGetGiveaway(slug as string);
    const { enterGiveaway } = useGiveaway(data?.id, false);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: useMemo(
            () => ({
                zip_code: '',
            }),
            []
        ),
    });

    const giveawayImage = useMemo(
        () => (
            <div className={styles['enter-giveaway__content']}>
                <div className={styles['enter-giveaway__img-wrapper']}>
                    <picture className={styles['enter-giveaway__img']}>
                        <source srcSet={data?.entry_page_image_url} media="(min-width: 1920px)" />
                        <source
                            srcSet={`${appendQueryParams(data?.entry_page_image_url, { key: 'dw', value: 3840 })} 2x, ${appendQueryParams(data?.entry_page_image_url, { key: 'dw', value: 1920 })} 1x`}
                            media="(min-width: 1440px)"
                        />
                        <source
                            srcSet={`${appendQueryParams(data?.entry_page_image_url, { key: 'dw', value: 2880 })} 2x, ${appendQueryParams(data?.entry_page_image_url, { key: 'dw', value: 1440 })} 1x`}
                            media="(min-width: 1024px)"
                        />
                        <source
                            srcSet={`${appendQueryParams(data?.entry_page_image_url, { key: 'dw', value: 2048 })} 2x, ${appendQueryParams(data?.entry_page_image_url, { key: 'dw', value: 1024 })} 1x`}
                            media="(min-width: 768px)"
                        />
                        <img
                            src={appendQueryParams(data?.entry_page_image_url, { key: 'dw', value: 768 })}
                            srcSet={`${appendQueryParams(data?.entry_page_image_url, { key: 'dw', value: 1536 })} 2x`}
                            alt={data?.title}
                        />
                    </picture>
                </div>
            </div>
        ),
        [data?.entry_page_image_url, data?.title]
    );

    const onSubmit = async (formData: { zip_code: string }) => {
        setIsSubmitting(true);
        try {
            const entryResponse = await enterGiveaway(formData.zip_code, getGiveawayCookie());
            setEntry(entryResponse.data);
            setShowBonus(true);
            // eslint-disable-next-line no-empty
        } catch (e) {
        }
        setIsSubmitting(false);
    };

    const copyUrl = useCallback((url: string) => {
        navigator.clipboard?.writeText(url);
        showToast({ type: 'success', message: 'Copied to clipboard.' });
    }, []);

    /* TODO: implement (ghost?) loading */
    if (isLoading) return null;

    if (!isLoading && error) return null;

    return (
        <>
            <Title platform={hub} title={data?.title} />
            <div className={styles['enter-giveaway__intro']}>
                <ReactModal
                    isOpen={showBonus}
                    className={classNames('react-modal__content', styles['enter-giveaway__modal-content'])}
                    overlayClassName="react-modal__overlay"
                    shouldCloseOnOverlayClick={false}
                    onRequestClose={closeModal}
                >
                    <form className={styles['enter-giveaway__modal-form']}>
                        <div className={styles['enter-giveaway__modal-header']}>
                            <button type="button" className={styles['enter-giveaway__modal-close-icon']} onClick={closeModal}>
                                <CloseIcon />
                            </button>
                        </div>
                        <div className={styles['enter-giveaway__modal-container']}>
                            <div className={styles['enter-giveaway__summary']}>
                                <h1 className={styles['enter-giveaway__title']}>Your entry was received</h1>
                                <ul className={styles['enter-giveaway__details']}>
                                    <li className={styles['enter-giveaway__detail']}>Get more chances to win by:</li>
                                    {data?.GiveawayShareLinks.map((link) => (
                                        <li className={styles['enter-giveaway__entry']} key={link.id}>
                                            <div className={styles['enter-giveaway__entry-text']}>{link.title}</div>
                                            <button
                                                type="button"
                                                className={classNames(styles['enter-giveaway__summary-cta'], styles['enter-giveaway__summary-cta--primary'], styles['enter-giveaway__summary-cta--share'])}
                                                onClick={() => {
                                                    window.open(`${supremeBasePath}/giveaway/${slug}/${entry?.share_token}/entry?type=${link.slug}`, '__blank');
                                                }}
                                            >
                                                {link.score === 1 ? '+1 Entry' : `+${link.score} Entries`}
                                            </button>
                                        </li>
                                    ))}
                                    <li className={styles['enter-giveaway__entry']}>
                                        <div className={styles['enter-giveaway__entry-text']}>Refer a friend</div>
                                        <button
                                            type="button"
                                            className={classNames(styles['enter-giveaway__summary-cta'], styles['enter-giveaway__summary-cta--primary'], styles['enter-giveaway__summary-cta--share'])}
                                            onClick={() => {
                                                // eslint-disable-next-line @typescript-eslint/dot-notation
                                                copyUrl(`${process.env['NEXT_PUBLIC_SUPREME_PLATFORM_URL']}d/g/${entry?.share_token}`);
                                            }}
                                        >
                                            +1 Entry
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </form>
                </ReactModal>

                <div className={styles['enter-giveaway__summary']}>
                    <h1 className={styles['enter-giveaway__title']}>{data?.title}</h1>
                    <ul className={styles['enter-giveaway__details']}>
                        <li className={styles['enter-giveaway__detail']}>This giveaway is for US residents only. Only one entry per person is permitted.</li>
                    </ul>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className={styles['enter-giveaway__form-container']}>
                            <input {...register('zip_code', { required: true })} type="text" placeholder="Zip Code" className={styles['enter-giveaway__input']} />
                            <span>{errors?.zip_code?.message}</span>
                        </div>
                        <div className={styles['enter-giveaway__summary-ctas']}>
                            <button disabled={isSubmitting} type="submit" className={classNames(styles['enter-giveaway__summary-cta'], styles['enter-giveaway__summary-cta--primary'])}>
                                Submit
                            </button>
                        </div>
                    </form>
                </div>
                {giveawayImage}
            </div>
        </>
    );
}
